import { useState, useMemo, useEffect, useCallback } from 'react';
import type { User, UserFilters } from '../types/user';
import { useRBAC } from './usePermissions';
import { useAuth } from './useAuth';
import { userService, GetUsersResponse } from '../services/userService';

interface UseUsersReturn {
  users: User[];
  filteredUsers: User[];
  loading: boolean;
  error: string | null;
  filters: UserFilters;
  setFilters: (filters: UserFilters) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  refreshUsers: () => Promise<void>;
  scope: 'organization' | 'all';
  canViewAllUsers: boolean;
  requestAllUsers: () => Promise<void>;
}

export const useUsers = (): UseUsersReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [filters, setFilters] = useState<UserFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [scope, setScope] = useState<'organization' | 'all'>('organization');
  const [totalCount, setTotalCount] = useState(0);
  
  const { hasPermission } = useRBAC();
  const { user } = useAuth();

  // Check if user can view all users (admin or system_operator)
  const canViewAllUsers = user?.roles?.includes('admin') || user?.roles?.includes('system_operator') || false;

  // Fetch users from backend
  const fetchUsers = useCallback(async (requestScope?: 'all') => {
    if (!hasPermission('users:read')) {
      // If no permission to read users, just show current user
      if (user) {
        setUsers([user as User]);
        setScope('organization');
        setTotalCount(1);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response: GetUsersResponse = await userService.getUsers(
        requestScope ? { scope: requestScope } : undefined
      );
      
      let usersList = response.users;
      
      // Ensure current user is always included in the list if they exist
      if (user && !usersList.find(u => u.id === user.id)) {
        usersList = [user as User, ...usersList];
      }
      
      setUsers(usersList);
      setScope(response.scope);
      setTotalCount(response.totalCount);
    } catch (err) {
      console.error('useUsers: Failed to fetch users:', err);
      setError('Failed to load users. Please try again.');
      // Fallback to showing current user if available
      if (user) {
        setUsers([user as User]);
        setScope('organization');
        setTotalCount(1);
      }
    } finally {
      setLoading(false);
    }
  }, [hasPermission, user]);

  // Function to request all users (admin override)
  const requestAllUsers = async () => {
    if (canViewAllUsers) {
      await fetchUsers('all');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Apply search and filters
  const filteredUsers = useMemo(() => {
    let result = users;

    // No need to track if current user is in results since we handle it in filters
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u => {
        // Always include current user in search results
        if (user && u.id === user.id) {
          return true;
        }
        
        const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim();
        const email = u.email || '';
        const orgName = u.organization?.name || '';
        
        return name.toLowerCase().includes(query) ||
               email.toLowerCase().includes(query) ||
               orgName.toLowerCase().includes(query);
      });
    }

    // Apply filters
    if (filters.role) {
      result = result.filter(u => {
        // Always include current user in role filter results
        if (user && u.id === user.id) {
          return true;
        }
        
        const userRole = u.role || (u.roles && u.roles[0]);
        return userRole === filters.role;
      });
    }

    if (filters.status) {
      result = result.filter(u => {
        // Always include current user in status filter results
        if (user && u.id === user.id) {
          return true;
        }
        
        const isActive = u.status === 'active' || u.isActive === true;
        const status = isActive ? 'active' : 'inactive';
        return status === filters.status;
      });
    }

    if (filters.organization) {
      result = result.filter(u => {
        // Always include current user in organization filter results
        if (user && u.id === user.id) {
          return true;
        }
        
        const orgName = u.organization?.name || '';
        return orgName.toLowerCase().includes(filters.organization!.toLowerCase());
      });
    }

    return result;
  }, [users, searchQuery, filters, user]);

  // Calculate statistics - use totalCount from backend for accurate count
  const totalUsers = totalCount || users.length;
  const activeUsers = users.filter(u => {
    const isActive = u.status === 'active' || u.isActive === true;
    return isActive;
  }).length;
  const inactiveUsers = totalUsers - activeUsers;

  return {
    users,
    filteredUsers,
    loading,
    error,
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
    totalUsers,
    activeUsers,
    inactiveUsers,
    refreshUsers: fetchUsers,
    scope,
    canViewAllUsers,
    requestAllUsers,
  };
}; 